import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { BUNDLE, money, HARNESS_SET, HARNESS_ONLY, LEASH_COLOURS, COLLAR_COLOURS } from "../lib/data";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const H_COLOURS = Object.keys(HARNESS_SET);
const H_SIZES = ["3XS", "XXS", "XS", "S", "M", "L"];
const L_COLOURS = Object.keys(LEASH_COLOURS);
const L_LENGTHS = ["3 m", "5 m"];
const C_COLOURS = Object.keys(COLLAR_COLOURS);
const C_SIZES = ["S", "M", "L"];

const Pills = ({ label, values, value, onChange, testid }) => (
  <div data-testid={testid}>
    <p className="font-manrope text-xs font-semibold uppercase tracking-wide text-ink">{label}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      {values.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          className={`rounded-full border px-4 py-1.5 font-manrope text-sm transition-colors ${
            value === v ? "border-charcoal bg-charcoal text-cream" : "border-charcoal/25 hover:border-charcoal"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  </div>
);

const Preview = ({ src, testid }) => (
  <div className="h-28 w-24 flex-shrink-0 overflow-hidden bg-white ring-1 ring-charcoal/10" data-testid={testid}>
    <img src={src} alt="" className="h-full w-full object-contain p-2" />
  </div>
);

export default function WalkKitBuilder({ trigger }) {
  const { add } = useCart();
  const [open, setOpen] = useState(false);
  const [h, setH] = useState({ Style: "Harness + Leash Set", Colour: "Blend Orange", Size: "M" });
  const [l, setL] = useState({ Length: "5 m", Colour: "Grey" });
  const [c, setC] = useState({ Colour: "Brown", Size: "M", name: "", phone: "" });

  const hImg = (h.Style === "Harness Only" ? HARNESS_ONLY : HARNESS_SET)[h.Colour];
  const lImg = LEASH_COLOURS[l.Colour];
  const cImg = COLLAR_COLOURS[c.Colour];

  const addKit = () => {
    if (!c.name || !c.phone) {
      toast.error("Add your dog's name and phone for the collar.");
      return;
    }
    const label = `Harness: ${h.Style}, ${h.Colour}, ${h.Size} · Leash: ${l.Length}, ${l.Colour} · Collar: ${c.Colour}, ${c.Size}, “${c.name}”`;
    add({
      slug: BUNDLE.slug,
      name: BUNDLE.name,
      price: BUNDLE.price,
      currency: BUNDLE.currency,
      image: cImg,
      qty: 1,
      options: {
        harness_style: h.Style, harness_colour: h.Colour, harness_size: h.Size,
        leash_length: l.Length, leash_colour: l.Colour,
        collar_colour: c.Colour, collar_size: c.Size, pet_name: c.name, phone: c.phone,
      },
      optionsLabel: label,
    });
    toast.success("The Walk Kit added to your bag");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto bg-cream sm:max-w-xl" data-testid="kit-builder">
        <DialogHeader>
          <DialogTitle className="font-fraunces text-3xl font-light">Build your Walk Kit</DialogTitle>
          <DialogDescription className="font-manrope text-sm text-ink">
            Pick your harness, leash and collar — then add all three as one kit and save.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="border-t border-charcoal/10 pt-4">
            <p className="mb-3 font-fraunces text-lg">1 · Harness + Leash</p>
            <div className="flex gap-4">
              <Preview src={hImg} testid="kit-harness-preview" />
              <div className="flex-1 space-y-3">
                <Pills label="Style" values={["Harness + Leash Set", "Harness Only"]} value={h.Style} onChange={(v) => setH({ ...h, Style: v })} testid="kit-harness-style" />
                <Pills label="Colour" values={H_COLOURS} value={h.Colour} onChange={(v) => setH({ ...h, Colour: v })} testid="kit-harness-colour" />
                <Pills label="Size" values={H_SIZES} value={h.Size} onChange={(v) => setH({ ...h, Size: v })} testid="kit-harness-size" />
              </div>
            </div>
          </div>

          <div className="border-t border-charcoal/10 pt-4">
            <p className="mb-3 font-fraunces text-lg">2 · Retractable Leash</p>
            <div className="flex gap-4">
              <Preview src={lImg} testid="kit-leash-preview" />
              <div className="flex-1 space-y-3">
                <Pills label="Length" values={L_LENGTHS} value={l.Length} onChange={(v) => setL({ ...l, Length: v })} testid="kit-leash-length" />
                <Pills label="Colour" values={L_COLOURS} value={l.Colour} onChange={(v) => setL({ ...l, Colour: v })} testid="kit-leash-colour" />
              </div>
            </div>
          </div>

          <div className="border-t border-charcoal/10 pt-4">
            <p className="mb-3 font-fraunces text-lg">3 · Personalized Collar</p>
            <div className="flex gap-4">
              <Preview src={cImg} testid="kit-collar-preview" />
              <div className="flex-1 space-y-3">
                <Pills label="Colour" values={C_COLOURS} value={c.Colour} onChange={(v) => setC({ ...c, Colour: v })} testid="kit-collar-colour" />
                <Pills label="Size" values={C_SIZES} value={c.Size} onChange={(v) => setC({ ...c, Size: v })} testid="kit-collar-size" />
                <div className="grid gap-3 sm:grid-cols-2">
                  <input placeholder="Dog's name" data-testid="kit-name" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} className="rounded-full border border-charcoal/20 bg-white px-4 py-2.5 font-manrope text-sm outline-none focus:border-rust" />
                  <input placeholder="Phone number" data-testid="kit-phone" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} className="rounded-full border border-charcoal/20 bg-white px-4 py-2.5 font-manrope text-sm outline-none focus:border-rust" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          onClick={addKit}
          data-testid="kit-builder-add"
          className="mt-6 w-full rounded-full bg-rust px-8 py-4 font-manrope font-semibold text-cream transition-colors hover:bg-charcoal"
        >
          Add the kit — {money(BUNDLE.price)}
          <span className="ml-2 text-cream/70 line-through">{money(BUNDLE.compareAt)}</span>
        </button>
      </DialogContent>
    </Dialog>
  );
}
